import { useState } from "react";
import { ShieldCheck, Car, Trophy } from "lucide-react";
import InsuranceComparisonTable from "./InsuranceComparisonTable";
import ComparisonTable from "./ComparisonTable";

/**
 * Iter-185.B (Sprint B · audit item H8) — Homepage tabbed comparison shelf.
 *
 * Before iter-185.B, the homepage rendered <InsuranceComparisonTable />
 * AND <ComparisonTable /> as two consecutive sibling sections. Each
 * carries its own ~150-line editorial wrapper (rubric strip + intro
 * paragraph + PartnerRateTable). Two consecutive table-heavy sections
 * pushed total scroll length on the homepage past where NerdWallet,
 * Bankrate, and The Zebra cap their primary table real estate.
 *
 * This shelf wraps both surfaces in a single section with a 2-tab
 * toggle (Insurance / Auto Loan). Insurance is the default tab — it's
 * the highest-CPC vertical and the more recent landing-intent visitor
 * is more likely to be insurance-shopping. The user can swap to the
 * auto-loan rubric with a single tap, matching the NerdWallet "Best
 * X" hub pattern.
 *
 * The existing <InsuranceComparisonTable /> + <ComparisonTable />
 * components are reused as-is — their <section> wrappers + testids +
 * editorial copy + rubric strips are preserved untouched. The shelf
 * just renders ONE of them based on tab state, hiding the inactive
 * one via display.
 *
 * Visual contract:
 *   - 2-tab toggle (Insurance / Auto Loan) in the slate-100 pill style
 *     matching the QuoteForm tab vocabulary.
 *   - Active tab: bg-slate-950 + white text.
 *   - Inactive tab: bg-transparent + slate-600 text.
 *   - Toggle sits in its own slate-50 wrapper above the inner section.
 *
 * Testids:
 *   - `homepage-comparison-shelf` — outer wrapper.
 *   - `homepage-comparison-shelf-tablist` — tab toggle group.
 *   - `homepage-comparison-shelf-tab-insurance` / `-auto-loan` — tab buttons.
 *   - The inner sections still carry their original testids
 *     (`insurance-comparison-table-section`, `comparison-table-section`)
 *     so existing pytest gates pass unchanged.
 */
const TABS = [
  {
    id: "insurance",
    label: "Auto Insurance",
    Icon: ShieldCheck,
  },
  {
    id: "auto-loan",
    label: "Auto Loans",
    Icon: Car,
  },
];

export default function HomepageComparisonShelf() {
  const [active, setActive] = useState("insurance");

  return (
    <div
      data-testid="homepage-comparison-shelf"
      data-active-tab={active}
      className="relative bg-white mt-4 lg:mt-6"
    >
      {/* Iter-185.W rev2 — Premium fintech section divider, second pass.
          User audit follow-up to the first iter-185.W treatment:
          "yellow-highlighted the gap — divider is invisible, big empty
          whitespace above COMPARISON SHELF."

          Fix: (a) tightened wrapper margin mt-10→mt-4 lg:mt-14→lg:mt-6
          so the divider sits closer to the heatmap (the heatmap's own
          py-16/24 already provides ample bottom padding), (b) bumped
          the emerald accent from h-[3px]→h-[5px] with stronger color
          stops (emerald-100→#10B981/55→emerald-100) so it's clearly
          visible against the surrounding white, (c) added a subtle
          slate-50 wash zone between the accent and the tab strip for
          a true "fresh card" feel like Stripe/Mercury section breaks.

          The layered structure reads top-to-bottom:
            5px emerald gradient sliver  ← brand cue
            1px slate-200 hairline       ← structural edge
            slate-50 transition zone     ← card-start feel
            tab strip on FAFAFA          ← begins new section */}
      <div
        data-testid="homepage-comparison-shelf-accent"
        aria-hidden
        className="absolute inset-x-0 top-0 h-[5px] bg-gradient-to-r from-emerald-100 via-[#10B981]/55 to-emerald-100 pointer-events-none"
      />
      <div
        data-testid="homepage-comparison-shelf-hairline"
        aria-hidden
        className="absolute inset-x-0 top-[5px] h-px bg-slate-200 pointer-events-none"
      />
      {/* Tab toggle row — sits in its own slate-50 strip above the
          inner editorial section. Mirrors the iter-90 PartnerRateTable
          sort-row aesthetic so the shelf reads as a comparison
          surface, not a generic page break. */}
      {/* Iter-190.J — User feedback: the tab strip's bg-[#FAFAFA]
          blended with the State Savings Heatmap directly below
          (which also uses bg-[#FAFAFA]), making the section
          handoff invisible. Switched to bg-white so the heatmap's
          gray now provides the natural section break. */}
      <div className="bg-white border-b border-slate-200 pt-1.5">
        <div className="max-w-wide mx-auto px-6 lg:px-10 py-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#10B981]">
                  Compare top picks
                </p>
                {/* Iter-277.B (Audit P2 #15) — Best-for / editor-ranked
                    trust badge. Unifies the shelf eyebrow with the
                    badge pattern PartnerRateTable + ProviderVsProvider
                    surfaces — every comparison surface now anchors
                    the "these are editorial picks, not paid
                    placement" affordance in a consistent visual
                    language (audit Phase 11 gap #3). */}
                <span
                  data-testid="homepage-comparison-shelf-editor-ranked-badge"
                  className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-[0.15em] text-[#0A2540] bg-[#10B981]/15 ring-1 ring-[#10B981]/30 px-1.5 py-0.5"
                >
                  <Trophy className="w-2.5 h-2.5" strokeWidth={2.5} aria-hidden />
                  Editor-ranked
                </span>
              </div>
              <p className="text-[12px] text-slate-600 leading-snug max-w-md">
                Compare auto insurers and auto lenders in one place.
                Switch tabs to see each ranking.
              </p>
            </div>
            <div
              role="tablist"
              aria-label="Comparison type"
              data-testid="homepage-comparison-shelf-tablist"
              className="inline-flex bg-slate-100 p-1 self-start sm:self-auto"
            >
              {TABS.map((t) => {
                const Icon = t.Icon;
                const isActive = active === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActive(t.id)}
                    data-testid={`homepage-comparison-shelf-tab-${t.id}`}
                    data-active={isActive ? "true" : "false"}
                    className={`min-h-[44px] inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors ${
                      isActive
                        ? "bg-slate-950 text-white"
                        : "bg-transparent text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 ${
                        isActive ? "text-[#10B981]" : "text-slate-500"
                      }`}
                      strokeWidth={2}
                    />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Active tab content — render-time conditional so React mounts
          one tree at a time. Both <section>s retain their original
          testids so iter-146/147 regression gates pass unchanged. We
          also strip the `border-y` from the inner section's own
          wrapper via a className override at this level (the outer
          shelf already carries its own divider). */}
      <div
        role="tabpanel"
        aria-labelledby={`homepage-comparison-shelf-tab-${active}`}
        data-testid={`homepage-comparison-shelf-panel-${active}`}
      >
        {active === "insurance" && <InsuranceComparisonTable />}
        {active === "auto-loan" && <ComparisonTable />}
      </div>
    </div>
  );
}
